import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService, Category } from '../services/api';
import { getValidImageUrl } from '../utils/imageUtils';
import { useCategoryTranslation } from '../utils/categoryTranslations';
import { useTranslations } from '../hooks/useTranslations';
import CustomerHeader from '../components/CustomerHeader';
import { useCartCount } from '../hooks/useCartCount';
import './CustomerHomePage.css';

const ProductsPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated: _isAuthenticated, user: _user } = useAuth();
  const t = useTranslations();
  const translateCategoryName = useCategoryTranslation();
  
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Use shared hook for cart count
  const cartCount = useCartCount(500);

  // Load all categories
  useEffect(() => {
    const loadCategories = async () => {
      try {
        setIsLoading(true);
        // Get all categories (level 3 categories that have products)
        const allCategories = await apiService.getCategoriesForSearch(3);
        console.log('📦 Loaded categories:', allCategories.length);
        setCategories(allCategories || []);
      } catch (error) {
        console.error('Error loading categories:', error);
        setCategories([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadCategories();
  }, []);

  const handleCategoryClick = (category: Category) => {
    navigate('/browse-all', {
      state: {
        categoryId: category.id,
        categoryName: category.name,
        filterType: 'category'
      }
    });
  };

  const getCategoryIcon = (categoryName: string): string => {
    const name = categoryName.toLowerCase();
    if (name.includes('sweet') || name.includes('mithai')) return '🍬';
    if (name.includes('bakery') || name.includes('bread')) return '🥖';
    if (name.includes('dairy') || name.includes('milk')) return '🥛';
    if (name.includes('fruit') || name.includes('vegetable')) return '🍎';
    if (name.includes('meat')) return '🥩';
    if (name.includes('flower')) return '🌸';
    if (name.includes('pickle')) return '🥒';
    if (name.includes('restaurant') || name.includes('food')) return '🍽️';
    return '📦';
  };

  const getCategoryImagePath = (_categoryName: string): string | null => {
    // Return null to use API image_url if available
    return null;
  };

  return (
    <div className="customer-home-page">
      {/* Header - Using CustomerHeader component for consistency */}
      <CustomerHeader
        variant="sticky"
        showTagline
        showVoiceSearch
        showProfileDropdown
        showBackButton={true}
        showLoyaltyButton
        cartCount={cartCount}
        onBackClick={() => navigate('/app')}
        onSearch={(query) => {
          navigate(`/search?q=${encodeURIComponent(query)}`);
        }}
        onCartClick={() => navigate('/app', { state: { showCart: true } })}
      />

      {/* Main Content */}
      <main id="main-content" role="main" style={{ paddingTop: '20px' }}>
        <div className="body-container">
          <h1 className="section-title-main" style={{ marginBottom: '32px' }}>
            {t.allProducts || 'All Products'}
          </h1>

          {isLoading ? (
            <div className="loading-state">Loading categories...</div>
          ) : categories.length === 0 ? (
            <div className="loading-state">No categories found</div>
          ) : (
            <div className="category-grid">
              {categories.map((category) => {
                const categoryImage = category.image_url 
                  ? getValidImageUrl(category.image_url) 
                  : (getCategoryImagePath(category.name) 
                    ? getValidImageUrl(getCategoryImagePath(category.name)!) 
                    : null);
                
                return (
                  <button
                    key={category.id}
                    className="category-button"
                    onClick={() => handleCategoryClick(category)}
                    aria-label={`${translateCategoryName(category.display_name || category.name)} category`}
                  >
                    {categoryImage ? (
                      <div className="category-image-wrapper">
                        <img 
                          src={categoryImage} 
                          alt={`${category.display_name || category.name} category`}
                          className="category-image"
                          loading="lazy"
                          decoding="async"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const parent = target.parentElement;
                            if (parent && !parent.querySelector('.category-icon-fallback')) {
                              const fallback = document.createElement('span');
                              fallback.className = 'category-icon-fallback';
                              fallback.textContent = getCategoryIcon(category.name);
                              parent.appendChild(fallback);
                            }
                          }}
                        />
                      </div>
                    ) : (
                      <span className="category-icon">{getCategoryIcon(category.name)}</span>
                    )}
                    <span className="category-name">
                      {translateCategoryName(category.display_name || category.name)}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ProductsPage;

